import { FC, useState, useRef, useMemo, useEffect } from 'react';
import type HotTable from '@handsontable//react';
import 'handsontable/dist/handsontable.full.css';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { successToast, errorToast } from '@/helpers';
import { ApiService } from '@/support/ApiService.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { _notNil, _isEmptyString, _isNil, _isNotBlank } from '@/littledash';
import {
  validateObservationTypeTitle,
  validateObservationTypeLabel,
  getBasicObservationTypeErrorMessage,
  getBasicLabelErrorMessage,
  ValidationData,
  parseScoringScale,
} from '@/utils/observationTypeValidation';
import type { ObservationTypeRow } from '@/utils/observationTypeValidation';
import { registerHOTModules } from '@/support/hot';
import SpreadSheet from '@/components/UI/SpreadSheet';
import { ScoringScaleEditor } from '@/components/UI/SpreadSheet/editors/ScoringScaleEditor';

registerHOTModules();
ScoringScaleEditor.register();

interface BulkAddObservationTypesProps {
  onSave: () => void;
  onCancel: () => void;
}

const BulkAddObservationTypes: FC<BulkAddObservationTypesProps> = ({ onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const hotTableRef = useRef<HotTable>();

  useEffect(() => {
    const fetchValidationData = async (): Promise<void> => {
      try {
        // Use the observation-specific validation endpoint
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/team-glossary/observation-validation-data',
          path: undefined,
          options: {
            onError: { toast: false, throw: true, capture: false, slug: 'bulk-add-observation-types-validation-load' },
          },
        });
        setValidationData(response.body as ValidationData);
      } catch (error) {
        errorToast('Failed to load validation data');
        ExceptionHandler.captureException(
          new InVivoError('Failed to load validation data', {
            cause: error,
            slug: 'bulk-add-observation-types-validation-load',
          })
        );
        setValidationData(null);
      } finally {
        setLoadingValidation(false);
      }
    };

    void fetchValidationData();
  }, []);

  function validateObservationType(value: string, rowIndex: number): { isValid: boolean; message?: string } {
    const hotInstance = hotTableRef.current?.hotInstance;
    const spreadsheetData = _notNil(hotInstance) ? (hotInstance.getData() as Array<ObservationTypeRow>) : [];

    return validateObservationTypeTitle(value, rowIndex, validationData, [], spreadsheetData);
  }

  function validateLabel(value: string, rowIndex: number): { isValid: boolean; message?: string } {
    const hotInstance = hotTableRef.current?.hotInstance;
    const spreadsheetData = _notNil(hotInstance) ? (hotInstance.getData() as Array<ObservationTypeRow>) : [];

    return validateObservationTypeLabel(value, rowIndex, validationData, [], spreadsheetData);
  }

  const initialData: Array<ObservationTypeRow | []> = useMemo(() => [['', '', '', ''], []], []);

  const columns = useMemo(
    () => [
      {
        data: 0,
        title: 'Observation type',
        type: 'text',
        className: 'htMiddle',
        width: 200,
        allowInvalid: true,
        validator: function (this: any, value: unknown, callback: (valid: boolean) => void) {
          if (_isNil(value) || typeof value !== 'string') {
            callback(false);
            return;
          }

          const rowIndex = this.row;
          if (typeof rowIndex !== 'number') {
            callback(true);
            return;
          }

          const validation = validateObservationType(value, rowIndex);
          callback(validation.isValid);
        },
        validationErrorMessage: (value: unknown) => {
          return getBasicObservationTypeErrorMessage(value as string, validationData);
        },
      },
      {
        data: 1,
        title: 'Label',
        type: 'text',
        className: 'htMiddle',
        width: 120,
        allowInvalid: true,
        validator: function (this: any, value: unknown, callback: (valid: boolean) => void) {
          if (_isNil(value)) {
            callback(true);
            return;
          }

          if (typeof value !== 'string') {
            callback(false);
            return;
          }

          const rowIndex = this.row;
          if (typeof rowIndex !== 'number') {
            callback(true);
            return;
          }

          const validation = validateLabel(value, rowIndex);
          callback(validation.isValid);
        },
        validationErrorMessage: (value: unknown) => {
          return getBasicLabelErrorMessage(value as string, validationData);
        },
      },
      {
        data: 2,
        title: 'Scoring scale',
        type: 'scoring-scale',
        className: 'htMiddle',
        width: 200,
        allowInvalid: true,
        // No validator needed - scoring scale is always optional
      },
      {
        data: 3,
        title: 'Grading information',
        type: 'text',
        className: 'htMiddle',
        width: 200,
        allowInvalid: true,
      },
    ],
    [validationData]
  );

  const hotTableSettings = useMemo(
    () => ({
      data: initialData,
      colHeaders: ['Observation type', 'Label', 'Scoring scale', 'Grading information'],
      columns,
      rowHeaders: true,
      manualColumnResize: true,
      contextMenu: {
        items: {
          row_above: {},
          row_below: {},
          remove_row: {},
        },
      },
      fillHandle: false,
      outsideClickDeselects: false,
      width: '100%',
      height: 400,
      rowHeights: 50,
      className: 'htMiddle',
      licenseKey: AppConfig.handsOnTableLicence,
      stretchH: 'all' as 'none' | 'all' | 'last' | undefined,
      minRows: 1,
      minSpareRows: 1,
      allowInsertRow: true,
    }),
    [initialData, columns]
  );

  async function handleSave(): Promise<void> {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    setSaving(true);

    try {
      const currentData = (hotInstance.getData?.() ?? []) as Array<ObservationTypeRow>;

      const validRows = currentData.filter((row) => {
        const [title] = row;
        return _isNotBlank(title);
      });

      if (validRows.length === 0) {
        errorToast('Please add at least one observation type');
        setSaving(false);
        return;
      }

      const teamGlossariesPayload = validRows.map((row) => {
        const [title, label, scoringScale, gradingInfo] = row;

        const maybeTitle = (_notNil(title) ? title.toString() : '').trim();
        const maybeLabel = (_notNil(label) ? label.toString() : '').trim();
        const maybeGradingInfo = (_notNil(gradingInfo) ? gradingInfo.toString() : '').trim();

        const scale = parseScoringScale(scoringScale as string);
        let type: 'none' | 'numeric' | 'severity' = 'none';
        let options: { min: string; max: string } | null = null;

        if (_notNil(scale)) {
          if (scale.type === 'numeric') {
            type = 'numeric';
            options = {
              min: scale.minValue.toString(),
              max: scale.maxValue.toString(),
            };
          } else if (scale.type === 'severity') {
            type = 'severity';
          }
        }

        return {
          title: maybeTitle,
          type,
          description: !_isEmptyString(maybeGradingInfo) ? maybeGradingInfo : null,
          group: 'observations' as const,
          ...(_notNil(options) && { options }),
          ...(!_isEmptyString(maybeLabel) && { label: maybeLabel }),
        };
      });

      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary/observations',
        path: undefined,
        body: {
          team_glossaries: teamGlossariesPayload,
        },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-add-observation-types-save' } },
      });

      successToast(`Successfully added ${validRows.length} observation type${validRows.length !== 1 ? 's' : ''}`);
      onSave();
    } catch (error) {
      errorToast('Failed to create observation types');
      ExceptionHandler.captureException(
        new InVivoError('Failed to create observation types', {
          cause: error,
          slug: 'bulk-add-observation-types-save',
        })
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalContainer className="bg-white" data-testid="bulk-add-observation-types-modal">
      <ModalHeader
        title="Create observation types"
        closeModal={onCancel}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />

      <div className="pa3">
        {loadingValidation ? (
          <div className="tc pa4" data-testid="loading-validation-data">
            <div className="mb2">Loading validation data...</div>
          </div>
        ) : (
          <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles" data-testid="observation-types-spreadsheet">
            <SpreadSheet
              className={'mt3'}
              data={initialData}
              settings={hotTableSettings}
              addCol={false}
              innerRef={hotTableRef}
              autoAddRow
              enableRowHeaderControls
              setInvalid={setHasValidationErrors}
              testPrefix="bulk-add-observation-types"
            />
          </div>
        )}
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={onCancel}
        submitBtnText="Create"
        cancelBtnText="Cancel"
        submitButtonProps={{
          loading: saving,
          disabled: saving || loadingValidation || hasValidationErrors,
          'data-testid': 'create-observation-types-submit',
        }}
        cancelButtonProps={{
          disabled: saving,
          'data-testid': 'create-observation-types-cancel',
        }}
      />
    </ModalContainer>
  );
};

export default BulkAddObservationTypes;
